import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title?: string;
  description: string;
  canonical: string;
  ogImage?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
  /** The same page in other languages (hreflang), including this one. */
  alternates?: { hrefLang: string; href: string }[];
  /** Open Graph locale, en_GB unless the page is in another language. */
  locale?: string;
}

export function SEOHead({
  title,
  description,
  canonical,
  ogImage = "https://migrada.eu/og-image.png",
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  noindex = false,
  jsonLd,
  alternates,
  locale = "en_GB",
}: SEOHeadProps) {
  const fullTitle = title ? `${title} | Migrada` : "Migrada";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {alternates?.map((a) => <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />)}

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Migrada" />
      <meta property="og:locale" content={locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article metadata */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {author && <meta property="article:author" content={author} />}

      {/* JSON-LD structured data */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
